import { z } from "zod";

// ---------------------------------------------------------------------------
// 単位の設定（#164、docs/66、DB 20261011_1900）。会社ごとの単位の一覧（表 units）。
//   ・名前は前後の空白を除いて 1〜6 文字（数えるのは文字＝㎡ も1文字。DB の CHECK と同じ）。同じ名前は1つ。1社 50 件まで
//   ・一覧の取得はだれでも（明細行の単位の欄の候補）、書くのは管理者だけ
//   ・明細行・品目は単位を文字で持つ（一覧を指さない）＝消す・名前を変える・標準に戻すをしても今ある行は変わらない
//   ・標準の一覧は DB の fn_units_default(製品, 業種) 1か所（ここには持たない）
//   ・欄の候補の絞り込みは unitCandidates 1か所（PC・スマホ・品目の欄が同じ関数）
// ---------------------------------------------------------------------------

/** 1社の上限（DB の fn_units_limit と同じ） */
export const UNIT_LIMIT = 50;
/** 名前の文字数の上限（DB の units_name_shape と同じ） */
export const UNIT_NAME_MAX = 6;

/** 単位の1行（GET /units の items） */
export const CompanyUnit = z.object({ id: z.string().uuid(), name: z.string(), sortNo: z.number().int() });
export type CompanyUnit = z.infer<typeof CompanyUnit>;
export const CompanyUnitList = z.object({ items: z.array(CompanyUnit) });
export type CompanyUnitList = z.infer<typeof CompanyUnitList>;

/** POST /units・PUT /units/:id（管理者）。決まりの確かめは unitNameError（API と画面が同じ関数） */
export const UnitNameBody = z.object({ name: z.string().max(200) });
export type UnitNameBody = z.infer<typeof UnitNameBody>;
/** POST /units/reorder（管理者）：並べたい順の id。載っていない単位はそのあとに今の順で並ぶ */
export const UnitReorder = z.object({ ids: z.array(z.string().uuid()).min(1).max(UNIT_LIMIT) });
export type UnitReorder = z.infer<typeof UnitReorder>;

export const UNIT_MSG = {
  title: "単位",
  name: "単位",
  add: "単位追加",
  reset: "標準に戻す",
  rename: "名前変更",
  delete: "削除",
  moveUp: "上へ",
  moveDown: "下へ",
  /** つまみの読み上げ（{name} の並びを変える） */
  grip: "「{name}」の並び",
  /** 確かめの窓のボタン（2026-10-11 射場さん） */
  ok: "決定",
  cancel: "取消",
  /** スマホ（幅 767px 以下）の単位名を押すと出るシート（名前変更・削除）の × の読み上げ */
  close: "閉じる",
  deleteTitle: "「{name}」を削除しますか",
  deleteNote: "作成済みの書類の単位は変わりません",
  resetTitle: "標準に戻しますか",
  resetNote: "単位の一覧を標準の一覧に置き換えます。作成済みの書類の単位は変わりません",
  readonly: "変更できるのは管理者のみです。",
  count: "{n}件／上限 {max}件",
  help: "見積・請求書の明細行と品目の単位の欄に、この順で候補として出ます。一覧にない単位も欄に直接入力できます（一覧には追加されません）。",
  empty: "単位を入力してください",
  tooLong: "6文字以内で入力してください",
  duplicate: "同じ単位がすでにあります",
  limit: "登録できるのは50件までです",
  notFound: "単位が見つかりません",
  added: "「{name}」を追加しました",
  renamed: "「{name}」に変更しました",
  deleted: "「{name}」を削除しました",
  resetDone: "標準の単位に戻しました",
} as const;

/** 入れた名前の前後の空白（半角・全角・タブ・改行）を除く */
export const unitNameOf = (raw: string): string => raw.trim();

/**
 * 名前の決まりに合うか。合わなければ画面に出す文（合えば null）。
 *   others＝ほかの単位の名前（名前変更のときは自分を除いた一覧）。同じ名前があれば「同じ単位がすでにあります」
 */
export function unitNameError(raw: string, others: readonly string[] = []): string | null {
  const name = unitNameOf(raw);
  if (!name) return UNIT_MSG.empty;
  if ([...name].length > UNIT_NAME_MAX) return UNIT_MSG.tooLong;
  if (others.includes(name)) return UNIT_MSG.duplicate;
  return null;
}

/** 頭の文字で絞り込むときの比べ方：全角・半角とつながった文字（㎡＝m2）をそろえ、英字は大小を見ない */
const fold = (s: string): string => s.normalize("NFKC").toLowerCase();

/**
 * 単位の欄の候補（#164）：会社の一覧を並び順どおり。品目の既定の単位（preferred）があれば先頭に。
 * 打った文字があれば、その文字で始まる単位だけ（「m」で m・㎡・㎥・m3）。打っていなければ全部
 */
export function unitCandidates(units: readonly string[], typed: string, preferred?: string | null): string[] {
  const head = preferred && preferred.trim() ? [preferred.trim()] : [];
  const list = [...head, ...units.filter((u) => !head.includes(u))];
  const q = fold(typed.trim());
  if (!q) return list;
  return list.filter((u) => fold(u).startsWith(q));
}
